import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { CompactLookup, type LookupAnswer } from '@features/CardsWorkspace';
import { cn } from '@shared/lib';

interface PhonePreviewProps {
  id: string;
  /** Слой превью открыт, и есть что показать. */
  open: boolean;
  answer: LookupAnswer | null;
  /** Прежний ответ, пока ищем новое слово или перечитываем для другого модуля: приглушён, не нажимается. */
  stale: boolean;
  word: string;
  /** Предел высоты от видимой области (`usePreviewRoom`). */
  maxHeight: number;
  errorText: string;
  onRetry?: () => void;
  /** Под счётчиками: «Жетоны закончились…». */
  note?: ReactNode;
  onChoose: () => void;
  onManual: () => void;
  /** Строка сверху страницы — превью под ней (2026-10-28). */
  below?: boolean;
}

/**
 * Превью этапа 1 на телефоне (ТЗ главной § 7): готовый `CompactLookup` над строкой, 8 px над полем,
 * ширина — как у поля. Поднимается снизу на 8 px с прозрачностью (200 мс), новая высота доезжает за
 * 220 мс (§ 13). Слово сверху и плашка «Выбрать, что учить» снизу видны при любой высоте.
 */
export function PhonePreview(props: PhonePreviewProps) {
  const { open, answer } = props;
  return (
    <AnimatePresence initial={false}>{open && answer && <PreviewCard key="preview" {...props} answer={answer} />}</AnimatePresence>
  );
}

function PreviewCard({
  id,
  answer,
  stale,
  word,
  maxHeight,
  errorText,
  onRetry,
  note,
  onChoose,
  onManual,
  below = false,
}: PhonePreviewProps & { answer: LookupAnswer }) {
  const { t } = useTranslation();
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  // Высота по содержимому: новый ответ меняет её плавно, без скачка.
  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const update = () => setHeight(el.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      id={id}
      role="region"
      aria-label={t('homeComposer.sheetAria', { word })}
      initial={{ opacity: 0, y: below ? -8 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: below ? -8 : 8 }}
      transition={{ duration: 0.2, ease: [0.2, 0.9, 0.2, 1] }}
      className={below ? 'absolute inset-x-0 top-2' : 'absolute inset-x-0 bottom-[calc(100%+8px)]'}
    >
      {/* Радиус обрезки 12 px — как у коротких ответов; у карточки ответа (16 px) он ничего не режет. */}
      <div className="hc-preview overflow-hidden rounded-[12px]" style={height === null ? undefined : { height }}>
        <div ref={innerRef} className={cn('transition-opacity duration-150', stale && 'pointer-events-none opacity-50')}>
          {/* solid — «Перевод не найден» на непрозрачной карточке: под превью затемнённая главная (приёмка A1-03). */}
          <CompactLookup
            state={answer}
            solid
            maxHeight={maxHeight}
            errorText={errorText}
            onRetry={onRetry}
            note={note}
            onChoose={onChoose}
            onManual={onManual}
          />
        </div>
      </div>
    </motion.div>
  );
}
